import "src/css/widgets/widgets.css";

import { connect } from 'react-redux';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTable } from "@fortawesome/pro-duotone-svg-icons";
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import MonitorWSService from 'src/services/monitorwsservice';
import { useTranslation } from 'react-i18next';
import WidgetSettings from "./widgetSettings"
import { removeWidget } from "src/redux/supervisorSlice";
import { selectionTypes } from "src/components/utils/consts";
import { DataGrid, Column, ColumnChooser, StateStoring, Sorting, SearchPanel, Scrolling, Paging } from 'devextreme-react/data-grid';
import DataSource from 'devextreme/data/data_source';
import PhoneStatus from "./agent/phonestatus";
import UserImage from "./agent/userimage";
import FormStatus from "./agent/formstatus";
import ChatStatus from "./agent/chatstatus";
import ButtonActions from "../agents/buttonactions";
import { seconds2HMS } from "../utils/utils";
import useMonitorAPIService from 'src/services/monitorapiservice';
import ArrayStore from "devextreme/data/array_store";
import WidgetTools from "./widgetTools";
import { setShowChat, selectChat } from "src/redux/chatSlice";
import { createRoot } from "react-dom/client";

function AgentsDashboard(props) {

    const agtsDataSource = useMemo(() => new DataSource({
        store: new ArrayStore({
            key: "pin",
            data: []
        }),
        reshapeOnPush: true
    }), []);

    const lastUpdate = useRef(new Date());
    lastUpdate.current.setMinutes(lastUpdate.current.getMinutes() - 1);
    const { t } = useTranslation();
    const { transferCallToAgent } = useMonitorAPIService();
    const gridRef = useRef(null);
    const [showSettings, setShowSettings] = useState(false);
    const [groupId, setGroupId] = useState(parseInt(localStorage.getItem(`AgentsDashboard_groupId_${props.identifier}_user${props.id}`) || -1));
    const [groupName, setGroupName] = useState(localStorage.getItem(`AgentsDashboard_groupName_${props.identifier}_user${props.id}`) || t("all2"));
    const agentsCountRef = useRef(null);
    const rowRoots = new Map()

    const ButtonActionsTemplate = (cellElement, cellInfo) => {
        const { data } = cellInfo;
        const rowId = `usrbtn_${data.pin}`;
        const old = rowRoots.get(rowId);

        if (old) {
            old.root.unmount();
            old.container.remove();
            rowRoots.delete(rowId);
        }

        const container = document.createElement("div");
        cellElement.append(container);
        const root = createRoot(container);

        root.render(<ButtonActions dashboard={true} agent={data}
            key={`agtbtns_${data.pin}`}
            token={props.token}
            record={props.record}
            id={props.id}
            functions={props.functions}
            setShowChat={props.setShowChat}
            selectChat={props.selectChat} />);

        rowRoots.set(rowId, { root, container });

    };

    const UserImageTemplate = (cellElement, cellInfo) => {

        const { data } = cellInfo;
        const rowId = `usrimg_${data.pin}`;
        const old = rowRoots.get(rowId);

        if (old) {
            old.root.unmount();
            old.container.remove();
            rowRoots.delete(rowId);
        }

        const container = document.createElement("div");
        cellElement.append(container);
        const root = createRoot(container);

        root.render(<UserImage dashboard={true} agent={data}
            maxPauseTime={props.maxPauseTime}
            excludeAgents={props.excludeAgents}
            notify={props.notify}
        />);

        rowRoots.set(rowId, { root, container });
    };

    const PhoneStatusTemplate = (cellElement, cellInfo) => {
        const { data } = cellInfo;
        const rowId = `phnsts_${data.pin}`;
        const old = rowRoots.get(rowId);

        if (old) {
            old.root.unmount();
            old.container.remove();
            rowRoots.delete(rowId);
        }

        const container = document.createElement("div");
        cellElement.append(container);
        const root = createRoot(container);

        root.render(<PhoneStatus key={`phonecell_${data.pin}`} agent={{
            strStatus: data.strStatus,
            ringing: data.ringing,
            incomingCall: data.incomingCall,
            dialing: data.dialing,
            hold: data.hold,
            currentCallAlarm: data.currentCallAlarm,
            currentCallDuration: data.currentCallDuration,
            holdTime: data.holdTime
        }} />);


        rowRoots.set(rowId, { root, container });
    };

    const FormStatusTemplate = (cellElement, cellInfo) => {

        const { data } = cellInfo;
        const rowId = `frmsts_${data.pin}`;
        const old = rowRoots.get(rowId);

        if (old) {
            old.root.unmount();
            old.container.remove();
            rowRoots.delete(rowId);
        }

        const container = document.createElement("div");
        cellElement.append(container);
        const root = createRoot(container);
        root.render(<FormStatus agent={data} />);

        rowRoots.set(rowId, { root, container });
    };

    const ChatStatusTemplate = (cellElement, cellInfo) => {
        const { data } = cellInfo;
        const rowId = `chtsts_${data.pin}`;
        const old = rowRoots.get(rowId);

        if (old) {
            old.root.unmount();
            old.container.remove();
            rowRoots.delete(rowId);
        }

        const container = document.createElement("div");
        cellElement.append(container);
        const root = createRoot(container);
        root.render(<ChatStatus agent={data} />);

        rowRoots.set(rowId, { root, container });
    };

    const contentUpdate = useCallback((e) => {
        lastUpdate.current = new Date();
        console.log("LAST UPDATE: ", lastUpdate.current)
    }, []);

    const grid = useMemo(() => {

        return (<DataGrid
            keyExpr="pin"
            key={props.identifier}
            id="agentsdashboard"
            ref={gridRef}
            dataSource={agtsDataSource}
            showBorders={false}
            repaintChangesOnly={true}
            columnAutoWidth={true}
            width={"100%"}
            height={"99%"}
            allowColumnResizing={true}
            rowAlternationEnabled={true}
            loadPanel={{ enabled: false }}
            rtlEnabled={props.rtl}
            cacheEnabled={false}
            onContentReady={contentUpdate}

        >

            <SearchPanel
                visible={true}
                width={240}
                placeholder={t("search")}
            />
            <Sorting mode={"single"} />
            <Scrolling mode={"virtual"} rowRenderingMode={"virtual"} />
            <Paging enabled={false} />
            <StateStoring
                enabled={true}
                type="localStorage"
                storageKey={`agentsdashboard_user${props.identifier}`}
            />
            <Column cssClass={"align-middle"} dataField="pin" visible={false} showInColumnChooser={false} />

            <Column cssClass={"align-middle"} dataField="actions" caption={""} cellTemplate={ButtonActionsTemplate} showInColumnChooser={false} width={60} />

            <Column cssClass={"align-middle"} dataField="statusId" defaultSortOrder="asc" caption={t("status")} allowSorting={true} cellTemplate={UserImageTemplate} showInColumnChooser={false} width={70} />

            <Column cssClass={"align-middle"} dataField="statusTooltip" caption={t("statusdescription")} visible={false} width={180} />
            <Column cssClass={"align-middle"} dataField="name" caption={t("username")} visible={false} width={100} />
            <Column cssClass={"align-middle"} dataField="fullName" caption={t("name")} width={100} />
            <Column cssClass={"align-middle"} dataField="ext" caption={t("extension")} width={60} />

            <Column cssClass={"align-middle"} dataField="strStatus" caption={t("phone")} cellTemplate={PhoneStatusTemplate} width={60} />
            <Column cssClass={"align-middle"} dataField="formOpen" caption={t("form")} cellTemplate={FormStatusTemplate} width={60} />
            <Column cssClass={"align-middle"} dataField="chats" caption={t("activechats")} cellTemplate={ChatStatusTemplate} width={60} />

            <Column cssClass={"align-middle"} dataField="totalChats" caption={t("totalchats")} width={60} />
            <Column cssClass={"align-middle"} dataField="incomingChats" caption={t("incomingchats")} visible={false} width={60} />
            <Column cssClass={"align-middle"} dataField="incomingChatsAttended" caption={t("incomingchatsattended")} visible={false} width={60} />
            <Column cssClass={"align-middle"} dataField="incomingChatsMissed" caption={t("incomingchatsmissed")} visible={false} width={60} />
            <Column cssClass={"align-middle"} dataField="outgoingChats" caption={t("outgoingchats")} visible={false} width={60} />
            <Column cssClass={"align-middle"} dataField="outgoingChatsDelivered" caption={t("outgoingchatsdelivered")} visible={false} width={60} />
            <Column cssClass={"align-middle"} dataField="outgoingChatsFailed" caption={t("outgoingchatsfailed")} visible={false} width={60} />

            <Column cssClass={"align-middle"} dataField="group" caption={t("group")} width={60} />
            <Column cssClass={"align-middle"} dataField="currentCallDurationStr" caption={t("currentcallduration")} width={80} />
            <Column cssClass={"align-middle"} dataField="interlocutor" caption={t("interlocutor")} width={100} />
            <Column cssClass={"align-middle"} dataField="interlocutorName" caption={t("interlocutorname")} visible={false} width={100} />
            <Column cssClass={"align-middle"} dataField="emailFrom" caption={t("email")} visible={false} width={60} />
            <Column cssClass={"align-middle"} dataField="country" caption={t("country")} visible={false} width={60} />
            <Column cssClass={"align-middle"} dataField="priority" caption={t("priority")} visible={false} width={60} />
            <Column cssClass={"align-middle"} dataField="calls" caption={t("calls")} width={60} />
            <Column cssClass={"align-middle"} dataField="incomingCalls" caption={t("incomings")} width={60} />
            <Column cssClass={"align-middle"} dataField="answeredCalls" caption={t("attended")} visible={false} width={60} />
            <Column cssClass={"align-middle"} dataField="missedCalls" caption={t("missed")} visible={false} width={60} />
            <Column cssClass={"align-middle"} dataField="missedPercentageStr" caption={t("missedpercentage")} visible={false} width={60} />
            <Column cssClass={"align-middle"} dataField="outgoingCalls" caption={t("outgoings")} width={60} />
            <Column cssClass={"align-middle"} dataField="totalCallsDurationStr" caption={t("callsduration")} visible={false} width={80} />
            <Column cssClass={"align-middle"} dataField="averageCallsDurationStr" caption={t("averagecallsduration")} visible={false} width={80} />
            <Column cssClass={"align-middle"} dataField="loginTimeStr" caption={t("registeredtime")} visible={false} width={80} />
            <Column cssClass={"align-middle"} dataField="availebleTimeStr" caption={t("availabletime")} visible={false} width={80} />
            <Column cssClass={"align-middle"} dataField="pauseTimeStr" caption={t("pausetime")} visible={false} width={80} />
            <Column cssClass={"align-middle"} dataField="firstLogin" caption={t("firstlogin")} dataType={"datetime"} visible={false} width={160} />
            <Column cssClass={"align-middle"} dataField="lastPause" caption={t("lastpause")} dataType={"datetime"} visible={false} width={160} />
            <Column cssClass={"align-middle"} dataField="lastPauseDurationStr" caption={t("lastpauseduration")} visible={false} width={80} />
            <Column cssClass={"align-middle"} dataField="timeOnThePhoneStr" caption={t("ocupation")} visible={false} width={80} />
            <Column cssClass={"align-middle"} dataField="idleDurationStr" caption={t("idle")} width={80} visible={false} />

            <ColumnChooser enabled={true} mode={"select"} allowSearch={true} />
        </DataGrid>);
    }, [props.rtl, props.identifier, agtsDataSource, t]);

    useEffect(() => {
        // component mounts
        const grid = gridRef.current?.instance;
        return () => {
            // component unmounts
            grid?.dispose();
            if (MonitorWSService?.stompClient?.connected) {
                MonitorWSService.stompClient.send("/app/unsubscribe/agentsdashboarddetail", {}, JSON.stringify({
                    token: props.token,
                }));

                MonitorWSService.stompClient.unsubscribe("agentsdashboarddetail");
            }

            rowRoots.forEach(({ root, container }) => {
                try { root.unmount() } catch { }
                try { container.remove() } catch { }
            });

            rowRoots.clear();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!props.connected) return;

        const sub = MonitorWSService.stompClient.send("/app/subscribe/agentsdashboarddetail", {}, JSON.stringify({
            token: props.token,
            groupId: groupId
        }));

        MonitorWSService.stompClient.subscribe(`/user/${props.token}/topic/agentsdashboarddetail/`, updateState, { id: "agentsdashboarddetail" });


        // eslint-disable-next-line react-hooks/exhaustive-deps
        return () => {
            sub?.unsubscribe();
        };
    }, [props.connected, props.token, groupId, updateState]);

    useEffect(() => {
        const gridEl = gridRef.current?.instance?.element();
        if (!gridEl) return;

        const handleDrop = event => {
            const rowEl = event.target.parentElement;
            if (rowEl && rowEl.classList.contains("dx-data-row")) {

                event.preventDefault();
                const dataStr = event.dataTransfer.getData("text/plain");
                const data = JSON.parse(dataStr);

                const rowIndex = parseInt(rowEl.getAttribute("aria-rowindex")) - 1
                const rowData = gridRef.current.instance.getVisibleRows()[rowIndex].data;
                const pin = rowData.pin;

                const callToTransfer = {
                    ext: data.ext?.trim() || data.portExt,
                    callId: data.id,
                    pin,
                    status: data.status,
                    forceTransfer: false
                };
                transferCallToAgent(props.token, callToTransfer, () => { });
            }
        };

        const handleDragOver = event => event.preventDefault();

        gridEl.addEventListener("drop", handleDrop);
        gridEl.addEventListener("dragover", handleDragOver);
        return () => {
            gridEl.removeEventListener("drop", handleDrop);
            gridEl.removeEventListener("dragover", handleDragOver);
        };
    }, [transferCallToAgent, props.token]);

    const toggleSettings = useCallback(() => {
        setShowSettings(prev => !prev);
    }, []);

    const setGroup = useCallback((groupId, groupName) => {

        setShowSettings(prev => !prev);
        setGroupId(groupId);
        setGroupName(groupName);

        try {
            localStorage.setItem(`AgentsDashboard_groupId_${props.identifier}_user${props.id}`, groupId);
            localStorage.setItem(`AgentsDashboard_groupName_${props.identifier}_user${props.id}`, groupName);
        } catch (err) {
            console.error("Failed to write to localStorage", err);
        }

        if (MonitorWSService.stompClient?.connected) {
            MonitorWSService.stompClient.send(
                "/app/subscribe/agentsdashboarddetail",
                {},
                JSON.stringify({ token: props.token, groupId })
            );
        }
    }, [props.identifier, props.id, props.token]);

    const closeWidget = useCallback(
        (e) => {
            e.preventDefault();
            props.removeWidget(props.identifier);
        },
        [props.close, props.identifier]
    );

    const updateState = useCallback(async (agentsJson) => {

        let lastUpdateDiff = (new Date() - lastUpdate.current);

        // if (document.hidden) {

        //     rowRoots.forEach(({ root, container }) => {
        //         try { root.unmount() } catch { }
        //         try { container.remove() } catch { }
        //     });

        //     rowRoots.clear();
        // }

        if (!document.hidden && lastUpdateDiff > 1000) {
            let showMenus = document.getElementById("dashboardwidget").getElementsByClassName("actions show").length > 0;
            if (!showMenus) {
                var data = JSON.parse(agentsJson.body);
                if (data?.length > 0) {

                    data.forEach(v => {
                        let userId = parseInt(v.pin);
                        v.currentCallAlarm = false;
                        v.pauseAlarm = false;
                        v.statusId = -1;

                        if (v.strAcdStatus === "Wrapup")
                            v.statusId = 0;

                        if (v.strAcdStatus === "Paused")
                            v.statusId = 1;

                        else if (v.strAcdStatus === "Unregistered")
                            v.statusId = 2;

                        else if (v.strAcdStatus === "LoggedOut")
                            v.statusId = 3;

                        let diff = 0
                        let acdStatusChanged = new Date();

                        let then = new Date();
                        then.setSeconds(then.getSeconds() - v.timeDiffAcdStatus);
                        acdStatusChanged = then;

                        let now = Date.now();
                        diff = Math.floor((now - then) / 1000);
                        v.timeDiffAcdStatus = diff;
                        v.idleDuration = 0;

                        let phoneDiff = 0;
                        then = new Date();
                        then.setSeconds(then.getSeconds() - v.currentCallDuration);
                        now = Date.now();
                        phoneDiff = (now - then) / 1000;

                        if (v.phoneStatusChanged !== null && v.strStatus === "Talking" && !v.dialing && !v.ringing) {
                            v.currentCallAlarm = props.maxConversation > 0 && phoneDiff > props.maxConversation && (!props.excludeAgents || props.excludeAgents.indexOf(userId) === -1);
                        }

                        if (v.phoneStatusChanged !== null && v.strStatus !== "Talking" && !v.dialing
                            && !v.ringing && v.strAcdStatus === "Free") {
                            v.idleDuration = phoneDiff > diff ? diff : phoneDiff;
                        }

                        if (v.strAcdStatus === "Paused") {
                            v.lastPauseDuration = Math.round((Date.now() - acdStatusChanged) / 1000);
                            v.lastPause = acdStatusChanged;
                        }

                        switch (v.strAcdStatus) {
                            case "Free":
                                v.statusTooltip = t('registered') + ' - ' + seconds2HMS(v.timeDiffAcdStatus);
                                break;
                            case "Paused":
                                v.pauseAlarm = false;
                                diff = Math.floor((Date.now() - v.lastPause) / 1000);
                                v.statusTooltip = t('paused');
                                if (v.pauseType !== null && v.pauseType !== "")
                                    v.statusTooltip += ' - ' + v.pauseType;

                                v.statusTooltip += ' - ' + seconds2HMS(diff);
                                v.pauseAlarm = (v.pauseExceeded || (props.maxPauseTime > 0 && diff > props.maxPauseTime)) && (!props.excludeAgents || props.excludeAgents.indexOf(userId) === -1);

                                break;
                            case 'Wrapup':
                                v.statusTooltip = t('wrapup');
                                break;
                            case 'Unregistered':
                                v.statusTooltip = t('unregistered');
                                break;
                            default:
                                v.statusTooltip = t('loggedout');
                                break;

                        };

                        v.formOpenAlarm = false;

                        if (v.formOpen && v.strStatus !== 'Talking')
                            v.formOpenAlarm = props.formOpenWithoutActivity > 0 && props.formOpenWithoutActivity < v.currentCallDuration && props.excludeAgents.indexOf(data.pin) === -1;

                        v.totalCallsDurationStr = seconds2HMS(v.totalCallsDuration);
                        v.averageCallsDurationStr = seconds2HMS(v.averageCallsDuration);
                        v.loginTimeStr = seconds2HMS(v.loginTime);
                        v.availebleTimeStr = seconds2HMS(v.availebleTime);
                        v.pauseTimeStr = seconds2HMS(v.pauseTime);
                        v.lastPauseDurationStr = seconds2HMS(v.lastPauseDuration);
                        v.timeOnThePhoneStr = seconds2HMS(v.timeOnThePhone);
                        v.idleDurationStr = seconds2HMS(v.idleDuration);
                        v.currentCallDurationStr = v.strStatus === 'Talking' ? seconds2HMS(v.currentCallDuration) : "";
                        v.missedPercentageStr = Math.round(v.missedPercentage) + "%";
                        v.timeOnThePhoneStr = Math.round(v.timeOnThePhone) + "%";
                    });

                    const fields = Object.keys(data[0]);
                    const oldData = agtsDataSource.items();
                    const changes = [];
                    const oldPins = new Set(agtsDataSource.items().map(x => x.pin));
                    const newPins = new Set(data.map(x => x.pin));

                    oldPins.forEach(x => {
                        if (!newPins.has(x)) {
                            changes.push({ type: 'remove', key: x });
                        }
                    });

                    data.forEach(v => {
                        if (oldPins.has(v.pin)) {
                            let oldAgent = oldData.find(x => x.pin === v.pin);
                            if (oldAgent && fields.some(field => oldAgent[field] !== v[field]))
                                changes.push({ type: 'update', key: v.pin, data: v });
                        } else {
                            changes.push({ type: 'insert', data: v });
                        }

                    });


                    if (changes?.length > 0) {
                        agtsDataSource.store().push(changes);
                    }


                    if (agentsCountRef.current)
                        agentsCountRef.current.innerText = data.length;

                }
            }
        }

    }, [agtsDataSource, props.maxConversation, props.excludeAgents]);


    return (
        <div className={"card card-flush overflow-hidden h-lg-20 widget-container"} id="dashboardwidget">
            <div className="card-header">
                <h3 className="card-title align-items-start flex-column text-primary drag-handle">
                    <span className="card-label fw-bold text-primary"><FontAwesomeIcon icon={faTable} /> {t("agentsdashboard")} - {groupName} <span className="badge badge-light-primary fs-base" ref={agentsCountRef}>0</span></span>
                </h3>
                <div className="card-toolbar" style={{ zIndex: 9999 }}>
                    <WidgetTools closeWidget={closeWidget} toggleSettings={toggleSettings}></WidgetTools>
                </div>
            </div>
            <div className="card-body" style={{ padding: "0px", height: "360px" }} >
                {grid}
            </div>
            <WidgetSettings showSettings={showSettings} toggleSettings={toggleSettings} token={props.token} setGroup={setGroup} groupId={groupId} groupName={groupName} selectionType={selectionTypes.GROUP}></WidgetSettings>
        </div>
    )
}

const mapStateToProps = (state) => {
    return {
        token: state.supervisor.token,
        connected: state.parameters.connected,
        maxPauseTime: state.parameters.maxPauseTime,
        id: state.supervisor.id,
        rtl: state.parameters.rtl,
        excludeAgents: state.parameters.excludeAgents,
        maxConversation: state.parameters.maxConversation,
        formOpenWithoutActivity: state.parameters.formOpenWithoutActivity,
        notify: state.parameters.notify,
        record: state.parameters.record,
        functions: state.supervisor.functions
    };
};

export default connect(mapStateToProps, { removeWidget, setShowChat, selectChat })(AgentsDashboard);